import type { InvoiceOrderInfo, InvoiceRecordItem } from "@/api/invoice";
import type { Invoice, InvoiceLineItem } from "./types";

function pickStatusStyle(status: number, statusText: string) {
  if (status === 2 || /已上传|已开具|已完成/.test(statusText)) {
    return { statusColor: "#10B981", statusBg: "#F0FDF4" };
  }
  if (status === 0 || /待处理|申请中|处理中/.test(statusText)) {
    return { statusColor: "#D97706", statusBg: "#FEF3C7" };
  }
  if (/驳回|失败|取消/.test(statusText)) {
    return { statusColor: "#EF4444", statusBg: "#FEF2F2" };
  }
  return { statusColor: "#6B7280", statusBg: "#F3F4F6" };
}

function formatIsoDateDisplay(iso: string): string {
  const s = (iso || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
}

function buildLineItems(
  record: InvoiceRecordItem,
  amount: number,
  orderInfo?: InvoiceOrderInfo | null,
): InvoiceLineItem[] {
  if (orderInfo && (orderInfo.hotel_name || orderInfo.order_no)) {
    const name = record.order_type_text || "订单";
    const hotelPart = [orderInfo.hotel_name, orderInfo.room_name]
      .filter((x) => x && String(x).trim())
      .join(" · ");
    const inD = formatIsoDateDisplay(orderInfo.check_in_date);
    const outD = formatIsoDateDisplay(orderInfo.check_out_date);
    const dates =
      inD && outD ? `入住 ${inD} · 离店 ${outD}` : inD || outD || "";
    const spec = [hotelPart, orderInfo.order_no, dates].filter(Boolean).join(" / ") || "—";
    return [
      {
        name,
        spec,
        qty: 1,
        unit: "单",
        price: amount,
        amount,
      },
    ];
  }

  const name = record.order_type_text || "发票项目";
  const specParts = [
    record.invoice_type_text,
    record.head_name,
    record.product_name,
    record.order_no,
  ].filter((x): x is string => Boolean(x && String(x).trim()));
  const spec = specParts.length ? [...new Set(specParts)].join(" · ") : "—";
  return [
    {
      name,
      spec,
      qty: 1,
      unit: "项",
      price: amount,
      amount,
    },
  ];
}

/** 是否已出票侧信息（有 PDF、发票代码或接口标记已上传等） */
export function recordHasIssuedAssets(record: InvoiceRecordItem): boolean {
  const url = (record.invoice_url || "").trim();
  if (url) return true;
  if (record.status === 2) return true;
  return Boolean(
    (record.invoice_code || "").trim() && (record.invoice_no || "").trim(),
  );
}

/** 是否视为「处理中」提示条（待处理且无成品票据信息） */
export function recordIsPendingLike(record: InvoiceRecordItem): boolean {
  if (recordHasIssuedAssets(record)) return false;
  return record.status === 0 || /待处理|处理中|申请/.test(record.status_text);
}

export function mapRecordToInvoice(record: InvoiceRecordItem): Invoice {
  const amount = Number(record.invoice_price) || 0;
  const { statusColor, statusBg } = pickStatusStyle(record.status, record.status_text);
  const title =
    (record.head_name || "").trim() ||
    (record.product_name || "").trim() ||
    record.order_type_text ||
    "发票申请";

  const hasIssuedMeta = recordHasIssuedAssets(record);
  const duty = (record.duty_number ?? "").trim();

  return {
    recordId: record.record_id,
    id: record.record_no,
    title,
    date: record.add_time,
    amount,
    type: [record.order_type_text, record.invoice_type_text].filter(Boolean).join(" · "),
    status: record.status_text,
    statusCode: record.status,
    statusColor,
    statusBg,
    taxNo: duty,
    email: (record.user_email ?? "").trim() || "—",
    invoiceCode: record.invoice_code || "",
    invoiceNumber: record.invoice_no || "",
    checkCode: record.check_code || "",
    seller: "",
    sellerTax: "",
    items: buildLineItems(record, amount, undefined),
    invoiceUrl: (record.invoice_url || "").trim() || undefined,
    buyerName:
      (record.user_name ?? "").trim() ||
      (record.head_name || "").trim() ||
      "—",
    buyerTypeText: record.type_text,
    invoiceHeadName: record.head_name,
    buyerDutyNumber: duty || undefined,
    userPhone: (record.user_phone ?? "").trim() || undefined,
    updateTime: record.update_time ?? undefined,
    hasIssuedMeta,
    showPendingTip: recordIsPendingLike(record),
  };
}

/** 详情接口 data → 展示模型（合并 order_info） */
export function mapDetailDataToInvoice(data: {
  record: InvoiceRecordItem;
  order_info: InvoiceOrderInfo | null;
}): Invoice {
  const { record, order_info } = data;
  const amount =
    Number(order_info?.total_amount) || Number(record.invoice_price) || 0;
  const base = mapRecordToInvoice(record);
  const title =
    [order_info?.hotel_name, order_info?.room_name]
      .filter((x) => x && String(x).trim())
      .join(" ")
      .trim() || base.title;

  return {
    ...base,
    title,
    amount,
    items: buildLineItems(record, amount, order_info),
    orderInfo: order_info ?? undefined,
  };
}
